/**
 * KeywordsPanel Component
 * Manages keywords for a specific ad group - extracted from KeywordRecommendations
 *
 * Key difference from KeywordRecommendations:
 * - Receives adGroup directly as prop (no dropdown selector needed)
 * - Fits within the EntityCard context
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import { useAuthStore } from '@/store/authStore';
import { usePendingChangesStore, getKeywordBidChangeKey, areBidsEqualInCents, getValidAppliedBid, isAppliedBidStale } from '@/store/pendingChangesStore';
import { ActionButtonGroup, AIRecommendationsButton, AddManuallyButton, Button, getAIButtonStatus } from '@/components/ui';
import { BulkManagementTable } from '@/components/common/BulkManagementTable';
import type { Campaign, AdGroup } from '@/types/campaign';
import { saveCampaignConfiguration } from '@/utils/saveConfiguration';
import { getErrorMessage } from '@/utils/errors';
import { debug, toast } from '@/utils';
import { formatEnumPretty } from '@/utils/formatEnum';
import apiClient from '@/services/api/client';
import { useRecommendationSelection } from '@/hooks/recommendations/useRecommendationSelection';
import { allowsManualCpcControl } from '@/types/adGroup';
import {
  StatusMessage,
  RecommendationsTable,
  RelevanceBar,
  SaveActionBar,
  type TableColumn,
  type StatusType,
} from '@/components/common/Recommendations';
import {
  formatNumberDisplay as formatMetricNumber,
  formatCurrencyDisplay as formatMetricCurrency,
} from '../utils';
import { useOptimizationConfig } from '@/hooks/useOptimizationConfig';

interface KeywordsPanelProps {
  adGroup: AdGroup;
  campaign: Campaign;
  selectedLandingPages: Map<string, boolean>;
}

interface KeywordRecommendation {
  keyword?: string;
  text?: string;
  match_type?: string;
  suggested_bid?: number;
  cpc_bid?: number;
  relevance_score?: number;
  reasoning?: string;
  source?: string;
}

interface ExistingKeyword {
  keyword_id: string;
  keyword_text: string;
  match_type: string;
  status: string;
  max_cpc_bid: number;
  // RMF compliance: keyword level metrics (28-day aggregates)
  impressions?: number;
  clicks?: number;
  cost?: number;
  conversions?: number;
}

// Max CPC Bid column header with info popover
function MaxCpcBidHeader({ canEditBids }: { canEditBids: boolean }) {
  const [showInfo, setShowInfo] = React.useState(false);

  return (
    <div className="flex items-center gap-1">
      <span>Max CPC Bid</span>
      <div className="relative">
        <button
          type="button"
          onClick={() => setShowInfo(!showInfo)}
          className="text-gray-400 hover:text-gray-600 focus:outline-none rounded-full transition-colors"
          aria-label="Max CPC Bid info"
          aria-expanded={showInfo}
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        </button>

        {showInfo && (
          <>
            {/* Backdrop to close on click outside */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setShowInfo(false)}
            />
            {/* Popover - positioned down and to the left */}
            <div className="absolute right-0 top-full mt-1 z-50 w-64">
              <div className="relative bg-gray-800 text-white text-xs rounded shadow-lg p-2 normal-case">
                <div className="absolute -top-1 right-2 w-2 h-2 bg-gray-800 rotate-45" />
                <p className="relative">
                  {canEditBids
                    ? 'Click a max CPC bid to edit. Press Enter to apply to your ads campaign.'
                    : 'Max CPC bids will be optimized daily to maximize your budget.'}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Inline editable bid cell component
interface EditableBidCellProps {
  keywordId: string;
  currentBid: number | undefined;
  isEditable: boolean;
  onSave: (keywordId: string, newBid: number) => Promise<void>;
}

function EditableBidCell({ keywordId, currentBid, isEditable, onSave }: EditableBidCellProps) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState(currentBid?.toFixed(2) || '');
  const [isSaving, setIsSaving] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  React.useEffect(() => {
    setEditValue(currentBid?.toFixed(2) || '');
  }, [currentBid]);

  const handleSave = async () => {
    const newBid = parseFloat(editValue);
    if (isNaN(newBid) || newBid <= 0) {
      setEditValue(currentBid?.toFixed(2) || '');
      setIsEditing(false);
      return;
    }

    if (newBid === currentBid) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(keywordId, newBid);
      setIsEditing(false);
    } catch {
      setEditValue(currentBid?.toFixed(2) || '');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(currentBid?.toFixed(2) || '');
      setIsEditing(false);
    }
  };

  if (!isEditable) {
    return (
      <span className="text-sm text-gray-600">
        ${currentBid?.toFixed(2) || 'N/A'}
      </span>
    );
  }

  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <span className="text-sm text-gray-600">$</span>
        <input
          ref={inputRef}
          type="number"
          step="0.01"
          min="0.01"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          disabled={isSaving}
          className="w-20 px-1 py-0.5 text-base border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        {isSaving && (
          <span className="text-xs text-gray-400">...</span>
        )}
      </div>
    );
  }

  return (
    <button
      onClick={() => setIsEditing(true)}
      className="text-sm text-gray-600 hover:text-blue-600 hover:underline cursor-pointer"
      title="Click to edit bid"
    >
      ${currentBid?.toFixed(2) || 'N/A'}
    </button>
  );
}

export function KeywordsPanel({
  adGroup,
  campaign,
  selectedLandingPages,
}: KeywordsPanelProps) {
  const queryClient = useQueryClient();
  const { memberUuid } = useAuthStore();
  // Use isFetching instead of isLoading because placeholderData makes isLoading report false
  const { data: optimizationConfig, isFetching: isConfigFetching } = useOptimizationConfig();
  const {
    queueKeywordBidChange,
    keywordBidChanges,
    appliedKeywordBids,
    registerDiscardCallback,
    unregisterDiscardCallback
  } = usePendingChangesStore(
    useShallow((state) => ({
      queueKeywordBidChange: state.queueKeywordBidChange,
      keywordBidChanges: state.changes.keywordBidChanges,
      appliedKeywordBids: state.appliedKeywordBids,
      registerDiscardCallback: state.registerDiscardCallback,
      unregisterDiscardCallback: state.unregisterDiscardCallback
    }))
  );


  // Get ad group ID
  const adGroupId = adGroup.ad_group_id || adGroup.ads_group_id || '';

  // State
  const [isGenerating, setIsGenerating] = useState(false);
  const [recommendations, setRecommendations] = useState<KeywordRecommendation[]>([]);
  const { selectedIndices, toggleItem, selectAll, deselectAll, clearSelection } = useRecommendationSelection();
  const [status, setStatus] = useState<{ type: StatusType; text: string } | null>(null);
  const [buttonStatus, setButtonStatus] = useState<string>('');

  // Manual add state
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [manualKeyword, setManualKeyword] = useState('');
  const [manualMatchType, setManualMatchType] = useState<'BROAD' | 'PHRASE' | 'EXACT'>('EXACT');
  const [manualBid, setManualBid] = useState('1.00');
  const [isAddingManual, setIsAddingManual] = useState(false);
  const [isSavingRecommendations, setIsSavingRecommendations] = useState(false);

  // Batch selection state
  const [selectedKeywords, setSelectedKeywords] = useState<Set<string>>(new Set());

  // Local state for optimistic updates
  const [localKeywordUpdates, setLocalKeywordUpdates] = useState<Map<string, { status?: 'ENABLED' | 'PAUSED'; removed?: boolean; max_cpc_bid?: number }>>(new Map());

  // Check if the ad group allows manual CPC control
  const canControlBids = allowsManualCpcControl(adGroup.bidding_strategy_type);

  // Check if bids can be manually edited:
  // - Must be Manual CPC bidding strategy (canControlBids)
  // - Config must be loaded (don't allow edits while loading to avoid brief fail-open window)
  // - AND either coldstart feature is disabled globally, OR flouri_optimize_bids is not enabled for this ad group
  const coldstartEnabled = optimizationConfig?.coldstartEnabled ?? false;
  const canEditBids = canControlBids && !isConfigFetching && (!coldstartEnabled || adGroup.flouri_optimize_bids !== true);

  // Get existing keywords with local optimistic updates and pending/applied bid changes
  // Priority order for bid values:
  // 1. Pending bid change (user edited but not yet saved)
  // 2. Applied bid change (saved but cache not yet updated)
  // 3. Local update (optimistic update from this component)
  // 4. Server value from adGroup.keywords_list
  const existingKeywords: ExistingKeyword[] = useMemo(() => {
    const keywords = adGroup.keywords_list || [];

    return keywords
      .filter(kw => {
        const update = localKeywordUpdates.get(kw.keyword_id);
        return !update?.removed;
      })
      .map(kw => {
        const update = localKeywordUpdates.get(kw.keyword_id);
        // Get pending bid change from the store's keywordBidChanges map using centralized key generation
        const customerId = campaign.google_ads_account_id;
        const key = getKeywordBidChangeKey(kw.keyword_id, campaign.campaign_id, adGroupId, customerId);
        const pendingBidChange = keywordBidChanges.get(key);
        // Get applied bid change using centralized staleness check
        const appliedBidChange = getValidAppliedBid(appliedKeywordBids, key);

        let updatedKw = kw;
        if (update?.status) {
          updatedKw = { ...updatedKw, status: update.status };
        }
        // Apply bid changes with priority: pending > applied > local > server
        if (pendingBidChange) {
          updatedKw = { ...updatedKw, max_cpc_bid: pendingBidChange.newBid };
        } else if (appliedBidChange) {
          // Use applied bid from store (persists across navigation)
          updatedKw = { ...updatedKw, max_cpc_bid: appliedBidChange.newBid };
        } else if (update?.max_cpc_bid !== undefined) {
          updatedKw = { ...updatedKw, max_cpc_bid: update.max_cpc_bid };
        }
        // Include metrics from the keyword data (RMF compliance)
        return {
          ...updatedKw,
          impressions: kw.impressions,
          clicks: kw.clicks,
          cost: kw.cost,
          conversions: kw.conversions,
        };
      });
  }, [
    adGroup.keywords_list,
    localKeywordUpdates,
    keywordBidChanges,
    appliedKeywordBids,
    campaign.campaign_id,
    campaign.google_ads_account_id,
    adGroupId,
  ]);

  // Clear state when ad group changes
  useEffect(() => {
    setSelectedKeywords(new Set());
    setLocalKeywordUpdates(new Map());
    setRecommendations([]);
    clearSelection();
    setStatus(null);
    setButtonStatus('');
  }, [adGroupId, clearSelection]);

  // Register discard callback for keyword bid changes
  // When user clicks "Discard", we need to clear local bid updates to revert to original values
  useEffect(() => {
    // Include campaign and account IDs to avoid collisions in multi-account scenarios
    const accountId = campaign.google_ads_account_id;
    const callbackKey = `keywords-panel-bids-${accountId ?? 'no-account'}-${campaign.campaign_id}-${adGroupId}`;

    // Clear local bid updates when changes are discarded, but only for keywords
    // that still have pending changes. In partial-save scenarios, some bids may
    // have been successfully applied (and cleared from the store) while others
    // failed. We should preserve local overrides for successfully-applied changes
    // to avoid UI snap-back until the next data sync refreshes the cache.
    registerDiscardCallback(callbackKey, () => {
      debug.log('Discarding keyword bid changes for ad group', adGroupId);

      // Skip bid change lookup if no account ID (can't have valid pending changes without it)
      if (!accountId) {
        setLocalKeywordUpdates(new Map());
        return;
      }

      // Get current pending bid changes from store
      const { changes } = usePendingChangesStore.getState();
      const pendingBidChanges = changes.keywordBidChanges;

      setLocalKeywordUpdates(prev => {
        const next = new Map<string, { status?: 'ENABLED' | 'PAUSED'; removed?: boolean; max_cpc_bid?: number }>();
        for (const [keywordId, update] of prev) {
          // Check if this keyword still has a pending bid change
          const changeKey = getKeywordBidChangeKey(keywordId, campaign.campaign_id, adGroupId, accountId);
          const pendingChange = pendingBidChanges.get(changeKey);

          if (pendingChange) {
            // Still has pending change - revert local bid override to the old bid.
            // We set max_cpc_bid to pendingChange.oldBid rather than just dropping
            // the override because oldBid reflects what the user saw when they started
            // editing (which may be a local override from a previous save if the cache
            // hasn't caught up yet). The reconciliation effect will clean up this
            // override once fresh server data arrives and matches.
            const { max_cpc_bid: _, ...rest } = update;
            next.set(keywordId, { ...rest, max_cpc_bid: pendingChange.oldBid });
          } else {
            // No pending change - this was either already saved or never changed
            // Keep the local override intact to prevent UI snap-back
            next.set(keywordId, update);
          }
        }
        return next;
      });
    });

    return () => {
      unregisterDiscardCallback(callbackKey);
    };
  }, [
    campaign.campaign_id,
    campaign.google_ads_account_id,
    adGroupId,
    registerDiscardCallback,
    unregisterDiscardCallback,
  ]);

  // Reconcile local bid overrides and applied bids when fresh keyword data arrives from the server.
  // This cleans up stale overrides that are no longer needed:
  // - When the server value matches the local/applied override (cache caught up after data sync)
  // - When there's no pending change for the keyword (override was for a completed save)
  useEffect(() => {
    const keywords = adGroup.keywords_list || [];
    if (keywords.length === 0) return;

    const accountId = campaign.google_ads_account_id;

    // Pre-index keywords by ID for O(1) lookups
    const keywordsByIdMap = new Map(
      keywords.map(kw => [kw.keyword_id, kw])
    );

    // Clean up local keyword updates
    setLocalKeywordUpdates(prev => {
      // Only process if we have local overrides to check
      if (prev.size === 0) return prev;

      // Skip bid change lookup if no account ID (can't have valid pending changes without it)
      if (!accountId) return prev;

      const { changes } = usePendingChangesStore.getState();
      const pendingBidChanges = changes.keywordBidChanges;

      let hasChanges = false;
      const next = new Map(prev);

      for (const [keywordId, update] of prev) {
        // Skip if there's no local bid override
        if (update.max_cpc_bid === undefined) continue;

        // Skip if there's still a pending change for this keyword
        const changeKey = getKeywordBidChangeKey(keywordId, campaign.campaign_id, adGroupId, accountId);
        if (pendingBidChanges.has(changeKey)) continue;

        // Find the server value for this keyword
        const serverKeyword = keywordsByIdMap.get(keywordId);
        if (!serverKeyword) continue;

        const serverBid = serverKeyword.max_cpc_bid || 0;

        // If server value matches local override, the cache has caught up - remove override
        if (areBidsEqualInCents(serverBid, update.max_cpc_bid)) {
          hasChanges = true;
          const { max_cpc_bid: _, ...rest } = update;
          if (Object.keys(rest).length === 0) {
            next.delete(keywordId);
          } else {
            next.set(keywordId, rest);
          }
        }
        // Note: If server differs from local override but there's no pending change,
        // the override may be stale (e.g., another client changed the bid).
        // We keep the override to avoid UI flicker, but it will be overwritten
        // on the next user edit or page refresh.
      }

      return hasChanges ? next : prev;
    });

    // Clean up applied bids from the store when server data catches up
    const { appliedKeywordBids, changes } = usePendingChangesStore.getState();
    const pendingBidChanges = changes.keywordBidChanges;
    const keysToRemove: string[] = [];

    // Normalize customer ID for comparison (strip non-digits to match key generation)
    const normalizedCustomerId = campaign.google_ads_account_id?.replace(/\D/g, '') || '';

    for (const [key, appliedBid] of appliedKeywordBids) {
      // Only process bids for this customer/campaign/adGroup
      const appliedCustomerId = appliedBid.customerId?.replace(/\D/g, '') || '';
      if (
        appliedCustomerId !== normalizedCustomerId ||
        appliedBid.campaignId !== campaign.campaign_id ||
        appliedBid.adGroupId !== adGroupId
      ) {
        continue;
      }

      // Remove stale entries using centralized staleness check
      if (isAppliedBidStale(appliedBid)) {
        keysToRemove.push(key);
        continue;
      }

      // Skip if there's still a pending change for this keyword
      if (pendingBidChanges.has(key)) continue;

      // Find the server value for this keyword
      const serverKeyword = keywordsByIdMap.get(appliedBid.keywordId);
      if (!serverKeyword) {
        // Keyword no longer exists in server data - clear stale applied bid
        keysToRemove.push(key);
        continue;
      }

      const serverBid = serverKeyword.max_cpc_bid || 0;

      // If server value matches the applied bid, the cache has caught up - remove from store
      if (areBidsEqualInCents(serverBid, appliedBid.newBid)) {
        keysToRemove.push(key);
      }
    }

    // Remove caught-up applied bids from the store (batch to avoid multiple rerenders)
    if (keysToRemove.length > 0) {
      usePendingChangesStore.getState().clearAppliedKeywordBidsByKeys(keysToRemove);
      debug.log('KeywordsPanel: Cleared applied bids that matched server data', {
        clearedCount: keysToRemove.length
      });
    }
  }, [adGroup.keywords_list, campaign.campaign_id, campaign.google_ads_account_id, adGroupId]);

  const handleGenerateRecommendations = async () => {
    if (!memberUuid) {
      setStatus({
        type: 'error',
        text: 'Authentication required. Please log in again.'
      });
      return;
    }

    setIsGenerating(true);
    setStatus(null);
    setButtonStatus('');

    try {
      const response = await apiClient.post('/keywords/recommendations/targeted', {
        member_uuid: memberUuid,
        campaign_id: campaign.campaign_id,
        ad_group_id: adGroupId
      }, { timeout: 120000 });

      const data = response.data;

      if (data.recommendations && data.recommendations.length > 0) {
        setRecommendations(data.recommendations);
        setButtonStatus(`Generated ${data.recommendations.length} keywords`);
      } else {
        setButtonStatus(data.message || 'No recommendations needed');
      }
    } catch (error) {
      console.error('Keyword generation error:', error);
      const errorMessage = getErrorMessage(error, 'Failed to generate recommendations');

      // Landing page access errors should show as toast to avoid layout shift
      if (errorMessage.includes('landing page') || errorMessage.includes('Landing page')) {
        toast.error(errorMessage);
      } else {
        setStatus({
          type: 'error',
          text: errorMessage
        });
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAddKeyword = async (keywordsToAdd: Array<{ text: string; match_type: string; cpc_bid: number }>) => {
    if (!memberUuid) {
      setStatus({ type: 'error', text: 'Authentication required. Please log in again.' });
      return;
    }

    setStatus({ type: 'info', text: 'Saving keywords to Google Ads...' });

    try {
      const response = await apiClient.post('/keywords/save', {
        member_uuid: memberUuid,
        campaign_id: campaign.campaign_id,
        ad_group_id: adGroupId,
        selected_keywords: keywordsToAdd,
        trigger_data_sync: true
      });

      const result = response.data;

      let message = result.message || 'Keywords saved to Google Ads successfully!';
      if (result.failed_keywords && result.failed_keywords.length > 0) {
        message += ` (${result.failed_keywords.length} failed)`;
      }

      toast.success(message);
      setStatus(null); // Clear inline status after success

      // Update campaign configuration
      const updatedCampaign = { ...campaign };
      if (updatedCampaign.ads_group_list) {
        updatedCampaign.ads_group_list = updatedCampaign.ads_group_list.map(ag => {
          const agId = ag.ad_group_id || ag.ads_group_id;
          if (agId === adGroupId) {
            const keywordsList = [...(ag.keywords_list || [])];

            for (const kw of result.successful_keywords || []) {
              const keywordText = kw.text || '';
              const matchType = kw.match_type || 'EXACT';

              const exists = keywordsList.some((existing: { text?: string; keyword_text?: string; match_type: string }) =>
                (existing.text === keywordText || existing.keyword_text === keywordText) &&
                existing.match_type === matchType
              );

              if (!exists) {
                keywordsList.push({
                  keyword_id: kw.keyword_id || `temp-${keywordText}-${matchType}`,
                  keyword_text: keywordText,
                  match_type: matchType,
                  status: 'ENABLED',
                  max_cpc_bid: kw.cpc_bid || 1.0
                });
              }
            }

            return { ...ag, keywords_list: keywordsList };
          }
          return ag;
        });
      }

      // Save configuration
      const selectedAdGroupsSet = new Set([adGroupId]);
      await saveCampaignConfiguration(updatedCampaign, selectedAdGroupsSet, undefined, queryClient);

      // Invalidate cache
      queryClient.invalidateQueries({ queryKey: ['campaign', campaign.campaign_id] });
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });

      return result;
    } catch (error) {
      console.error('Save keywords error:', error);
      setStatus({
        type: 'error',
        text: error instanceof Error ? error.message : 'Failed to save keywords'
      });
      throw error;
    }
  };

  const handleSaveRecommendations = async () => {
    if (selectedIndices.size === 0) {
      setStatus({ type: 'error', text: 'Please select at least one keyword to save.' });
      return;
    }

    setIsSavingRecommendations(true);
    const keywordsToSave = Array.from(selectedIndices)
      .map(index => recommendations[index])
      .map(rec => ({
        text: (rec.keyword || rec.text)!,
        match_type: rec.match_type || 'BROAD',
        cpc_bid: rec.suggested_bid || rec.cpc_bid || 1.0
      }));

    try {
      const result = await handleAddKeyword(keywordsToSave);

      // Remove saved keywords from recommendations list
      const savedKeywordTexts = new Set(
        result.successful_keywords.map((kw: { text?: string; keyword?: string }) => kw.text || kw.keyword).filter(Boolean)
      );
      setRecommendations(prev =>
        prev.filter(rec => {
          const keywordText = rec.keyword || rec.text;
          return keywordText && !savedKeywordTexts.has(keywordText);
        })
      );

      clearSelection();
    } catch {
      // Error already handled
    } finally {
      setIsSavingRecommendations(false);
    }
  };

  const handleAddManualKeyword = async () => {
    if (!manualKeyword.trim()) {
      setStatus({ type: 'error', text: 'Please enter a keyword' });
      return;
    }

    const bid = parseFloat(manualBid);
    if (isNaN(bid) || bid <= 0) {
      setStatus({ type: 'error', text: 'Please enter a valid bid amount' });
      return;
    }

    setIsAddingManual(true);
    try {
      await handleAddKeyword([{
        text: manualKeyword.trim(),
        match_type: manualMatchType,
        cpc_bid: bid
      }]);

      setManualKeyword('');
      setManualBid('1.00');
      setShowManualAdd(false);
    } catch {
      // Error already handled
    } finally {
      setIsAddingManual(false);
    }
  };

  const handleKeywordToggle = (keywordId: string) => {
    setSelectedKeywords(prev => {
      const next = new Set(prev);
      if (next.has(keywordId)) {
        next.delete(keywordId);
      } else {
        next.add(keywordId);
      }
      return next;
    });
  };

  const handleUpdateKeywordBid = async (keywordId: string, newBid: number) => {
    // Ensure the user is authenticated before queuing a bid change
    // Throw errors (not just return) so EditableBidCell stays in edit mode on failure
    if (!memberUuid) {
      const msg = 'Session expired. Please sign in again.';
      setStatus({ type: 'error', text: msg });
      throw new Error(msg);
    }

    // Ensure we have a specific Google Ads customer ID for this campaign
    // Without this, the bid change could be applied to the wrong account for multi-account users
    if (!campaign.google_ads_account_id) {
      const msg = 'Unable to edit keyword bids: campaign is not linked to a Google Ads account.';
      setStatus({ type: 'error', text: msg });
      throw new Error(msg);
    }

    // Prevent bid editing for keywords that don't yet have a numeric Google Ads criterion ID.
    // Table rows for unsynced keywords may use temporary, non-numeric IDs (e.g., 'temp-123').
    if (!keywordId || !/^\d+$/.test(keywordId)) {
      const msg = 'Cannot edit bid for unsaved keyword. Please wait for sync to complete.';
      setStatus({ type: 'error', text: msg });
      throw new Error(msg);
    }

    // Find the keyword to get its text
    const keyword = adGroup.keywords_list?.find(
      kw => kw.keyword_id === keywordId
    );
    if (!keyword) {
      const msg = 'Keyword not found';
      setStatus({ type: 'error', text: msg });
      throw new Error(msg);
    }

    // Compute the baseline "oldBid" for discard/revert purposes.
    // If there's already a pending change, preserve its original oldBid so that
    // multiple edits before saving (e.g., 1→2→3) will still revert to 1, not 2.
    // Otherwise, derive oldBid from applied bid, local override, or raw adGroup value.
    const changeKey = getKeywordBidChangeKey(keywordId, campaign.campaign_id, adGroupId, campaign.google_ads_account_id);
    const pendingChange = keywordBidChanges.get(changeKey);
    // Use the store's selector which enforces TTL on applied bids
    const appliedBidChange = usePendingChangesStore.getState().getAppliedKeywordBid(
      keywordId, campaign.campaign_id, adGroupId, campaign.google_ads_account_id
    );
    const localUpdate = localKeywordUpdates.get(keywordId);
    let oldBid: number;
    if (pendingChange) {
      // Preserve the original baseline from the existing pending change
      oldBid = pendingChange.oldBid;
    } else if (appliedBidChange) {
      // Use the applied bid as the baseline (from a previous successful save, not yet in cache)
      oldBid = appliedBidChange.newBid;
    } else if (localUpdate?.max_cpc_bid !== undefined) {
      // Use the local override as the baseline (from a previous successful save)
      oldBid = localUpdate.max_cpc_bid;
    } else {
      // Fall back to the raw adGroup value
      oldBid = keyword.max_cpc_bid || 0;
    }

    // Queue the bid change instead of making an immediate API call
    // keywordId is already validated as numeric above
    queueKeywordBidChange({
      keywordId,
      campaignId: campaign.campaign_id,
      adGroupId,
      keywordText: keyword.keyword_text || '',
      oldBid,
      newBid,
      customerId: campaign.google_ads_account_id
    });

    // Also update local state for optimistic UI updates that persist after save
    // This ensures the UI shows the new bid even after the pending store is cleared
    // Only persist a local override if the bid actually differs at cents precision from the baseline
    setLocalKeywordUpdates(prev => {
      const next = new Map(prev);
      const existing = next.get(keywordId) || {};

      if (!areBidsEqualInCents(oldBid, newBid)) {
        next.set(keywordId, { ...existing, max_cpc_bid: newBid });
      } else if (next.has(keywordId)) {
        // Reverted to original value: drop the local override
        const { max_cpc_bid: _, ...rest } = existing;
        if (Object.keys(rest).length === 0) {
          next.delete(keywordId);
        } else {
          next.set(keywordId, rest);
        }
      }
      return next;
    });

    debug.log('Queued keyword bid change', { keywordId, oldBid, newBid, customerId: campaign.google_ads_account_id });
  };

  const handleSelectAllKeywords = () => {
    if (selectedKeywords.size === existingKeywords.length) {
      setSelectedKeywords(new Set());
    } else {
      setSelectedKeywords(new Set(existingKeywords.map(k => k.keyword_id)));
    }
  };

  const handleExecuteBatchAction = async (action: 'enable' | 'pause' | 'remove', itemIds: string[]) => {
    if (!memberUuid) return;

    // Filter out temporary/unsynced keyword IDs (non-numeric).
    // Only keywords that have been synced to Google Ads have numeric criterion IDs.
    const numericIds = itemIds.filter(id => /^\d+$/.test(id));
    const skippedCount = itemIds.length - numericIds.length;

    if (numericIds.length === 0) {
      setStatus({
        type: 'error',
        text: 'Selected keywords have not been synced to Google Ads yet. Please wait for data sync to complete.'
      });
      return;
    }

    const actionName = action === 'enable' ? 'enabling' : action === 'pause' ? 'pausing' : 'removing';
    const skippedMsg = skippedCount > 0
      ? ` (${skippedCount} unsynced keyword${skippedCount > 1 ? 's' : ''} skipped)`
      : '';
    setStatus({ type: 'info', text: `${actionName.charAt(0).toUpperCase() + actionName.slice(1)} ${numericIds.length} keyword${numericIds.length > 1 ? 's' : ''}${skippedMsg}...` });

    try {
      const response = await apiClient.post('/keywords/status/update', {
        member_uuid: memberUuid,
        campaign_id: campaign.campaign_id,
        ad_group_id: adGroupId,
        keyword_ids: numericIds,
        action: action
      });

      const result = response.data;

      // Optimistic update for keywords that were actually sent to the API
      const updates = new Map(localKeywordUpdates);
      numericIds.forEach(keywordId => {
        if (action === 'remove') {
          updates.set(keywordId, { removed: true });
        } else if (action === 'enable') {
          updates.set(keywordId, { status: 'ENABLED' });
        } else if (action === 'pause') {
          updates.set(keywordId, { status: 'PAUSED' });
        }
      });
      setLocalKeywordUpdates(updates);

      toast.success(result.message);
      setStatus(null); // Clear inline status after success

      // Update campaign configuration
      const updatedCampaign = { ...campaign };
      if (updatedCampaign.ads_group_list) {
        updatedCampaign.ads_group_list = updatedCampaign.ads_group_list.map(ag => {
          const agId = ag.ad_group_id || ag.ads_group_id;
          if (agId === adGroupId && ag.keywords_list) {
            const updatedKeywords = ag.keywords_list
              .map((kw) => {
                if (numericIds.includes(kw.keyword_id)) {
                  if (action === 'remove') return null;
                  if (action === 'enable') return { ...kw, status: 'ENABLED' as const };
                  if (action === 'pause') return { ...kw, status: 'PAUSED' as const };
                }
                return kw;
              })
              .filter((kw): kw is NonNullable<typeof kw> => kw !== null);

            return { ...ag, keywords_list: updatedKeywords };
          }
          return ag;
        });
      }

      try {
        // Build selectedAdGroups set based on actual flouri_optimize_bids values
        // to preserve the current optimization settings
        const selectedAdGroupsForSave = new Set<string>();
        updatedCampaign.ads_group_list?.forEach(ag => {
          const agId = ag.ad_group_id || ag.ads_group_id || '';
          if (ag.flouri_optimize_bids === true) {
            selectedAdGroupsForSave.add(agId);
          }
        });

        await saveCampaignConfiguration(
          updatedCampaign,
          selectedAdGroupsForSave,
          selectedLandingPages,
          queryClient
        );
        debug.log('Campaign configuration updated after keyword status change');
      } catch (error) {
        console.error('Failed to save updated campaign configuration:', error);
      }

      setSelectedKeywords(new Set());

      await queryClient.invalidateQueries({ queryKey: ['campaign', campaign.campaign_id] });
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      await queryClient.refetchQueries({ queryKey: ['campaign', campaign.campaign_id] });

    } catch (error) {
      console.error('Batch action error:', error);
      setStatus({
        type: 'error',
        text: error instanceof Error ? error.message : `Failed to ${action} keywords`
      });
      throw error;
    }
  };

  // Define table columns for recommendations
  const tableColumns: TableColumn<KeywordRecommendation>[] = [
    {
      header: 'Keyword',
      key: 'keyword',
      minWidth: '16rem',
      render: (rec) => (
        <div>
          <p className="text-sm font-medium text-gray-900">{rec.keyword || rec.text || 'N/A'}</p>
          {rec.reasoning && (
            <p className="text-xs text-gray-500 mt-1 italic">{rec.reasoning}</p>
          )}
        </div>
      ),
    },
    {
      header: 'Match Type',
      key: 'match_type',
      render: (rec) => (
        <span className="px-2 py-1 rounded-md bg-gray-100 text-xs font-medium">
          {formatEnumPretty(rec.match_type || 'BROAD')}
        </span>
      ),
    },
    {
      header: 'Relevance',
      key: 'relevance',
      render: (rec) => (
        <RelevanceBar score={rec.relevance_score || 0} className="text-sm text-gray-600" />
      ),
    },
  ];

  return (
    <div className="p-4">
      {/* Status Message */}
      {status && (
        <StatusMessage type={status.type} message={status.text} className="mb-4" />
      )}

      {/* Action Buttons */}
      <ActionButtonGroup
        statusMessage={getAIButtonStatus(isGenerating, 'keywords', buttonStatus) || undefined}
        isLoading={isGenerating}
        className="mb-4"
      >
        <AIRecommendationsButton
          onClick={handleGenerateRecommendations}
          disabled={isGenerating}
          isLoading={isGenerating}
        />
        <AddManuallyButton
          onClick={() => setShowManualAdd(!showManualAdd)}
        />
      </ActionButtonGroup>

      {/* Manual Add Form */}
      {showManualAdd && (
        <div className="mb-4 p-4 border rounded-lg bg-gray-50">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Add Keyword</h4>
          <div className={`grid grid-cols-1 gap-4 ${canControlBids ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
            <div className="md:col-span-2">
              <label htmlFor="manual-keyword-panel" className="block text-sm font-medium text-gray-700 mb-1">
                Keyword Text
              </label>
              <input
                id="manual-keyword-panel"
                type="text"
                value={manualKeyword}
                onChange={(e) => setManualKeyword(e.target.value)}
                placeholder="e.g., running shoes"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
            <div>
              <label htmlFor="manual-match-type-panel" className="block text-sm font-medium text-gray-700 mb-1">
                Match Type
              </label>
              <select
                id="manual-match-type-panel"
                value={manualMatchType}
                onChange={(e) => setManualMatchType(e.target.value as 'BROAD' | 'PHRASE' | 'EXACT')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="EXACT">Exact</option>
                <option value="PHRASE">Phrase</option>
                <option value="BROAD">Broad</option>
              </select>
            </div>
            {canControlBids && (
              <div>
                <label htmlFor="manual-bid-panel" className="block text-sm font-medium text-gray-700 mb-1">
                  Max CPC Bid ($)
                </label>
                <input
                  id="manual-bid-panel"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={manualBid}
                  onChange={(e) => setManualBid(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            )}
          </div>
          {!canControlBids && (
            <p className="mt-2 text-xs text-gray-600">
              This ad group uses <strong>{formatEnumPretty(adGroup.bidding_strategy_type)}</strong> bidding.
              Google automatically manages bids.
            </p>
          )}
          <div className="mt-4 flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={handleAddManualKeyword}
              disabled={!manualKeyword.trim() || isAddingManual}
            >
              {isAddingManual ? 'Adding...' : 'Add Keyword'}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setShowManualAdd(false);
                setManualKeyword('');
                setManualBid('1.00');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Current Keywords Section */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">
          Current Keywords ({existingKeywords.length})
        </h3>

        {existingKeywords.length === 0 ? (
          <div className="text-center py-6 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-gray-600">No keywords in this ad group yet</p>
            <p className="text-sm text-gray-500 mt-1">Use AI recommendations or add keywords manually</p>
          </div>
        ) : (
          <BulkManagementTable
            items={existingKeywords.map((keyword, index) => ({
              id: keyword.keyword_id || `temp-${keyword.keyword_text}-${keyword.match_type}-${index}`,
              text: keyword.keyword_text,
              status: keyword.status as 'ENABLED' | 'PAUSED' | 'REMOVED',
              match_type: keyword.match_type,
              max_cpc_bid: keyword.max_cpc_bid,
              // RMF compliance: keyword level metrics
              impressions: keyword.impressions,
              clicks: keyword.clicks,
              cost: keyword.cost,
              conversions: keyword.conversions,
            }))}
            selectedItems={selectedKeywords}
            onToggleItem={handleKeywordToggle}
            onSelectAll={handleSelectAllKeywords}
            onBulkAction={handleExecuteBatchAction}
            itemLabel="keyword"
            columns={[
              {
                header: 'Keyword',
                key: 'text',
                render: (item) => <span className="text-sm text-gray-900">{item.text}</span>
              },
              {
                header: 'Match Type',
                key: 'match_type',
                render: (item) => (
                  <span className="px-2 py-1 rounded-md bg-gray-100 text-xs font-medium">
                    {formatEnumPretty(item.match_type)}
                  </span>
                )
              },
              ...(canControlBids ? [{
                header: <MaxCpcBidHeader canEditBids={canEditBids} />,
                key: 'max_cpc_bid',
                render: (item: { id: string; max_cpc_bid?: number }) => (
                  <EditableBidCell
                    keywordId={item.id}
                    currentBid={item.max_cpc_bid}
                    isEditable={canEditBids}
                    onSave={handleUpdateKeywordBid}
                  />
                )
              }] : []),
              {
                header: 'Clicks',
                key: 'clicks',
                render: (item: { clicks?: number }) => (
                  <span className="text-sm text-gray-600">{formatMetricNumber(item.clicks)}</span>
                )
              },
              {
                header: 'Avg CPC',
                key: 'avg_cpc',
                render: (item: { clicks?: number; cost?: number }) => {
                  const avgCpc = item.clicks && item.clicks > 0 && item.cost !== undefined
                    ? item.cost / item.clicks
                    : undefined;
                  return <span className="text-sm text-gray-600">{formatMetricCurrency(avgCpc)}</span>;
                }
              },
            ]}
            showStatus={true}
            allowedActions={['enable', 'pause', 'remove']}
          />
        )}
      </div>

      {/* AI Recommendations Table */}
      {recommendations.length > 0 && (
        <div className="mt-4">
          <RecommendationsTable
            items={recommendations}
            columns={tableColumns}
            selectedItems={selectedIndices}
            onToggleItem={toggleItem}
            onSelectAll={() => selectAll(recommendations.length)}
            onDeselectAll={deselectAll}
            itemCount={recommendations.length}
            itemLabel="Keywords"
          />

          <SaveActionBar
            selectedCount={selectedIndices.size}
            itemLabel="keyword"
            onSave={handleSaveRecommendations}
            isSaving={isSavingRecommendations}
          />
        </div>
      )}
    </div>
  );
}

export default KeywordsPanel;
